package com.essntl.features.invoice.ui.addeditinvoice.components.content

import androidx.compose.foundation.layout.*
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import com.essntl.features.paymentparameter.domain.model.PaymentParameterModel
import com.essntl.features.invoice.ui.addeditinvoice.price.applyPaymentParameters
import com.essntl.features.invoice.ui.addeditinvoice.price.getStripeProducts
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.checkbox.EsPrimaryCheckBox
import com.essntl.uikit.components.dialog.EsDialog
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography

@Composable
fun CreatePaymentLinkDialog(
    isOpened: Boolean,
    onDismissRequest: () -> Unit,
    balance: Int,
    loading: Boolean,
    paymentParameterList: List<PaymentParameterModel>,
    productName: String,
    addPaymentLink: (amount: Int, paymentParameterIds: Set<String>) -> Unit,
) {
    if (!isOpened)
        return

    EsDialog(
        title = "Create Payment Link",
        onDismissRequest = onDismissRequest,
    ) {
        CreatePaymentLinkDialogContent(
            balance = balance,
            loading = loading,
            productName = productName,
            paymentParameterList = paymentParameterList,
            addPaymentLink = { amount, paymentParameterIds ->
                addPaymentLink(amount, paymentParameterIds)
                onDismissRequest()
            },
        )
    }
}

@Composable
fun CreatePaymentLinkDialogContent(
    balance: Int,
    loading: Boolean,
    productName: String,
    paymentParameterList: List<PaymentParameterModel>,
    addPaymentLink: (amount: Int, paymentParameterIds: Set<String>) -> Unit,
) {
    var selectedPaymentParameterSet by remember {
        mutableStateOf(emptySet<String>())
    }

    val selectedApplyToAmountPaymentParameter = remember(selectedPaymentParameterSet) {
        paymentParameterList.filter { p ->
            p.applyToAmount == true &&
                p.id in selectedPaymentParameterSet
        }
    }

    val selectedStripeProductParameter = remember(selectedPaymentParameterSet) {
        paymentParameterList.filter { p ->
            p.applyToAmount == false &&
                p.method == PaymentParameterModel.Method.Add &&
                p.id in selectedPaymentParameterSet
        }
    }

    val amount = remember(selectedApplyToAmountPaymentParameter) {
        if (selectedApplyToAmountPaymentParameter.isNotEmpty())
            applyPaymentParameters(
                servicesTotalPrice = balance,
                paymentParameterList = selectedApplyToAmountPaymentParameter,
            )
        else
            balance
    }

    val validator = remember {
        CreatePaymentLinkValidator()
    }

    val stripeProducts = remember(selectedPaymentParameterSet) {
        getStripeProducts(
            invoiceDocNumber = productName,
            servicesTotalPrice = amount,
            paymentParameterList = selectedStripeProductParameter,
        )
    }

    LaunchedEffect(Unit) {
        validator.amount.value = amount

        // check by default the payment parameters that apply to the amount
        selectedPaymentParameterSet = paymentParameterList
            .filter { it.applyToAmount == false}
            .map { it.id }
            .toSet()
    }

    Column(modifier = Modifier.padding(2.spacing)) {

        // Checkboxes for payment parameters
        paymentParameterList.forEach { paymentParameter ->
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Start,
                modifier = Modifier.fillMaxWidth(),
            ) {
                EsPrimaryCheckBox(
                    checked = paymentParameter.id in selectedPaymentParameterSet,
                    onCheckedChange = {
                        if (it)
                            selectedPaymentParameterSet += paymentParameter.id
                        else
                            selectedPaymentParameterSet -= paymentParameter.id
                    },
                )

                Spacer(modifier = Modifier.width(2.spacing))

                Text(
                    text = paymentParameter.name.orEmpty(),
                    style = MaterialTheme.esTypography.p1Medium16,
                )
            }

            Spacer(modifier = Modifier.height(2.spacing))
        }

        Spacer(modifier = Modifier.height(4.spacing))

        // Stripe products to create
        stripeProducts.forEach { product ->
            Row(
                horizontalArrangement = Arrangement.SpaceBetween,
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text(
                    text = product.first,
                    style = MaterialTheme.esTypography.p1Regular16,
                    color = MaterialTheme.esColorScheme.textPrimary,
                )

                Text(
                    text = "$${product.second / 100.0}",
                    style = MaterialTheme.esTypography.p1Regular16,
                    color = MaterialTheme.esColorScheme.textPrimary,
                )
            }

            Spacer(modifier = Modifier.height(2.spacing))
        }

        Spacer(modifier = Modifier.height(1.spacing))

        // Total
        Row(
            horizontalArrangement = Arrangement.SpaceBetween,
            modifier = Modifier.fillMaxWidth(),
        ) {
            Text(
                text = "Total",
                style = MaterialTheme.esTypography.p1Bold16,
                color = MaterialTheme.esColorScheme.textPrimary,
            )

            Text(
                text = "$${
                    stripeProducts.sumOf { (_, price) -> price } / 100.0
                }",
                style = MaterialTheme.esTypography.p1Bold16,
                color = MaterialTheme.esColorScheme.textPrimary,
            )
        }

        Spacer(modifier = Modifier.height(3.spacing))

        EsLargePrimaryButton(
            text = "Add",
            onClick = {
                if (validator.validate())
                    addPaymentLink(amount, selectedPaymentParameterSet)
            },
            loading = loading,
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 1.spacing),
        )
    }
}